import App from "@/App";
import { UserProvider } from "@/context/UserContext";
import { useTheme } from "@/context/useTheme";
import { useTranslationLoader } from "@/hooks/useTranslationLoader";
import { restoreGluceraAuthSessionFromPWA } from "@/lib/authSession";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, Spin } from "antd";
import enUS from "antd/es/locale/en_US";
import hiIN from "antd/es/locale/hi_IN";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});

const getAntdLocale = (lng: string) => {
  switch (lng) {
    case "hi":
      return hiIN;
    case "en":
    default:
      return enUS;
  }
};

export const ConfigWrapper = () => {
  const { theme } = useTheme();
  const { i18n } = useTranslation();
  const { isLoading, isLoaded } = useTranslationLoader();
  const antdLocale = getAntdLocale(i18n.language);
  const [pwaRestored, setPwaRestored] = useState(false);

  // Restore PWA session on app startup with timeout
  useEffect(() => {
    const restorePWA = async () => {
      try {
        const timeoutPromise = new Promise((resolve) => {
          setTimeout(() => resolve(false), 2000); // 2 second timeout
        });

        const restorePromise = restoreGluceraAuthSessionFromPWA();
        
        await Promise.race([restorePromise, timeoutPromise]);
      } catch (error) {
        console.error("PWA restore error:", error);
      } finally {
        setPwaRestored(true);
      }
    };
    
    void restorePWA();
  }, []);

  // Show loading spinner while translations are being loaded or PWA is restoring
  if ((isLoading && !isLoaded) || !pwaRestored) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        hashed: false,
        token: {
          fontFamily: "var(--font-family)",
          fontSize: 16,
        },
        ...theme,
      }}
      componentSize="middle"
      // Global date format configuration
      datePicker={{
        format: "DD-MM-YYYY",
      }}
    >
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <App />
        </UserProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );
};
